import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Card, 
  Typography, 
  Alert, 
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Email, Lock } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { BRAND } from '../theme/brandTheme';
import { getDashboardHomePath } from '../utils/roleUtils';
import '../components/dashboard/ReportListing.css';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { login, error: authError } = useAuth();
  const navigate = useNavigate();

  // LocalStorage'dan remember me durumunu ve kullanıcı adını kontrol et
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (savedRememberMe && savedUsername) {
      setFormData(prev => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRememberMeChange = (e) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);
    
    if (!isChecked) {
      localStorage.removeItem('rememberedUsername');
      localStorage.removeItem('rememberMe');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Form doğrulama
    if (!formData.username.trim() || !formData.password.trim()) {
      setFormError('Kullanıcı adı ve şifre gereklidir.');
      return;
    }
    
    // Remember me işlevselliği
    if (rememberMe) {
      localStorage.setItem('rememberedUsername', formData.username);
      localStorage.setItem('rememberMe', 'true');
    }
    
    setLoading(true);
    
    try {
      const result = await login(formData.username, formData.password);
      if (result?.success) {
        navigate(getDashboardHomePath(result.user?.role));
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card 
      sx={{ 
        width: '100%', 
        maxWidth: '850px', 
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
        border: `1px solid ${BRAND.borderLight}`,
      }}
    >
      {/* Brand panel — matches landing hero purple */}
      <Box
        sx={{
          width: isMobile ? '100%' : '40%',
          bgcolor: BRAND.purple,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: isMobile ? 3 : 4,
          minHeight: isMobile ? '140px' : 'auto',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src={BRAND.logoWhite}
          alt="Hype"
          sx={{ height: { xs: 28, md: 36 }, width: 'auto' }}
        />
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: 14,
            textAlign: 'center',
            fontFamily: BRAND.fontFamily,
            maxWidth: 220,
            lineHeight: 1.5,
          }}
        >
          Tıklamaları gelire çeviren modern optimizasyon
        </Typography>
      </Box>

      {/* Sağ form alanı */}
      <Box 
        sx={{ 
          width: isMobile ? '100%' : '60%', 
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, mb: 1, color: BRAND.purple, fontFamily: BRAND.fontFamily }}
          >
            Dashboard Girişi
          </Typography>
          <Typography variant="body1" sx={{ color: BRAND.textMuted, fontFamily: BRAND.fontFamily }}>
            Tekrar hoş geldiniz, hesabınıza giriş yapın.
          </Typography>
        </Box>
        
        {(authError || formError) && (
          <Alert severity="error" sx={{ 
            mb: 3, 
            borderRadius: '5px',
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}>
            {formError || authError}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Kullanıcı Adı
            </Typography>
            <TextField
              required
              fullWidth
              id="username"
              placeholder="Kullanıcı adı"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleChange}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  '&:hover': {
                    backgroundColor: '#f0f1f2'
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white'
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: BRAND.purple,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: BRAND.purple }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Şifre
            </Typography>
            <TextField
              required
              fullWidth
              name="password"
              placeholder="••••••••"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  '&:hover': {
                    backgroundColor: '#f0f1f2'
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white'
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: BRAND.purple,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: BRAND.purple }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            alignItems: isMobile ? 'flex-start' : 'center'
          }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  sx={{
                    color: BRAND.purple,
                    '&.Mui-checked': { color: BRAND.purple },
                  }}
                />
              }
              label="Beni hatırla"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
            />
            <Link href="#" underline="hover" sx={{ color: BRAND.purple, textDecoration: 'none', fontSize: '0.9rem' }}>
              Şifremi Unuttum
            </Link>
          </Box>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3.5,
              mb: 1,
              py: 1.5,
              borderRadius: '8px',
              fontWeight: 700,
              boxShadow: 'none',
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Giriş Yap'}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default Login; 